const POLICY_SITE_BASE = 'https://www.7day.com.au';

const INTERNAL_POLICY_LINKS: Record<string, { label: string; url: string }> = {
    'Terms of Payment': {
        label: 'View Terms of Payment',
        url: `${POLICY_SITE_BASE}/terms-of-payment`,
    },
    'Privacy Policy': {
        label: 'View Privacy Policy',
        url: `${POLICY_SITE_BASE}/privacy-policy`,
    },
};

function toAbsoluteUrl(href: string): string {
    if (href.startsWith('http://') || href.startsWith('https://')) {
        return href;
    }

    if (href.startsWith('/')) {
        return `${POLICY_SITE_BASE}${href}`;
    }

    return href;
}

function setLinkDisplay(element: HTMLElement, label: string, url: string) {
    element.replaceChildren();
    element.appendChild(document.createTextNode(`${label}:`));
    element.appendChild(document.createElement('br'));
    element.appendChild(document.createTextNode(url));
}

function formatLinksForPrint(clone: HTMLElement) {
    clone.querySelectorAll('[role="button"]').forEach((node) => {
        const text = node.textContent?.trim() ?? '';
        const mapping = INTERNAL_POLICY_LINKS[text];
        if (!mapping) return;

        const parent = node.parentElement;
        if (!parent) return;

        if (parent.textContent?.trim() === `View ${text}`) {
            setLinkDisplay(parent, mapping.label, mapping.url);
            return;
        }

        const replacement = document.createElement('span');
        setLinkDisplay(replacement, mapping.label, mapping.url);
        node.replaceWith(document.createElement('br'), replacement);
    });

    clone.querySelectorAll('a').forEach((anchor) => {
        const href = anchor.getAttribute('href') ?? '';
        const linkText = anchor.textContent?.trim() ?? '';

        if (href.startsWith('mailto:')) {
            anchor.replaceWith(href.replace('mailto:', ''));
            return;
        }

        if (!href) return;

        const absoluteUrl = toAbsoluteUrl(href);
        if (linkText === absoluteUrl || linkText === href) {
            anchor.replaceWith(absoluteUrl);
            return;
        }

        const replacement = document.createElement('span');
        const label = linkText.charAt(0).toUpperCase() + linkText.slice(1);
        setLinkDisplay(replacement, label, absoluteUrl);
        anchor.replaceWith(replacement);
    });
}

const POLICY_PRINT_STYLES = `
    body { font-family: Inter, Arial, sans-serif; margin: 24px; color: #1f3550; font-size: 13px; line-height: 1.5; }
    h1 { margin: 0 0 12px 0; font-size: 22px; font-weight: 700; text-transform: uppercase; }
    p, li { margin: 10px 0; }
    ul { margin: 10px 0; padding-left: 24px; }
    strong, b { font-weight: 700; }
    a { color: #1f3550; text-decoration: underline; }
    .policy-section-title { font-weight: 700; font-size: 14px; }
`;
function copyComputedTypographyStyles(source: HTMLElement, clone: HTMLElement) {
    const sourceNodes = source.querySelectorAll('.MuiTypography-root');
    const cloneNodes = clone.querySelectorAll('.MuiTypography-root');

    sourceNodes.forEach((sourceEl, index) => {
        const cloneEl = cloneNodes[index] as HTMLElement | undefined;
        if (!cloneEl) return;

        const { fontWeight, textTransform } = window.getComputedStyle(sourceEl);
        const weight = parseInt(fontWeight, 10);

        if (weight >= 600 || fontWeight === 'bold') {
            cloneEl.style.fontWeight = '700';
        }

        if (textTransform && textTransform !== 'none') {
            cloneEl.style.textTransform = textTransform;
        }
    });
}

function getPrintableHtml(element: HTMLElement): string {
    const clone = element.cloneNode(true) as HTMLElement;
    clone
        .querySelectorAll('.policy-document-actions')
        .forEach((el) => el.remove());
    copyComputedTypographyStyles(element, clone);
    formatLinksForPrint(clone);
    return clone.innerHTML;
}

function openPrintFrame(title: string, bodyHtml: string) {
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow?.document;
    const frameWindow = printFrame.contentWindow;
    if (!frameDoc || !frameWindow) {
        printFrame.remove();
        return;
    }

    frameDoc.open();
    frameDoc.write(`
        <html>
        <head>
            <title>${title}</title>
            <style>${POLICY_PRINT_STYLES}</style>
        </head>
        <body>${bodyHtml}</body>
        </html>
    `);
    frameDoc.close();

    const cleanup = () => {
        setTimeout(() => {
            printFrame.remove();
        }, 0);
    };

    frameWindow.onafterprint = cleanup;
    setTimeout(() => {
        frameWindow.focus();
        frameWindow.print();
    }, 150);
    setTimeout(cleanup, 2000);
}

export function printPolicyDocument(element: HTMLElement, title: string) {
    openPrintFrame(title, getPrintableHtml(element));
}
